import React from 'react';
import './StatusCard.css';

export type StatusType = 'connected' | 'disconnected' | 'warning' | 'loading';

interface StatusCardProps {
  title: string;
  icon: string;
  status: StatusType;
  accountInfo?: string;
  additionalInfo?: string;
  onConnect?: () => void;
  onDisconnect?: () => void;
  connectLabel?: string;
  isLoading?: boolean;
  warningMessage?: string;
}

export const StatusCard: React.FC<StatusCardProps> = ({
  title,
  icon,
  status,
  accountInfo,
  additionalInfo,
  onConnect,
  onDisconnect,
  connectLabel = 'Connect',
  isLoading = false,
  warningMessage,
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'connected':
        return {
          className: 'status-card--connected',
          badge: 'Connected',
          badgeClass: 'status-badge--success',
        };
      case 'disconnected':
        return {
          className: 'status-card--disconnected',
          badge: 'Not Connected',
          badgeClass: 'status-badge--error',
        };
      case 'warning':
        return {
          className: 'status-card--warning',
          badge: 'Warning',
          badgeClass: 'status-badge--warning',
        };
      case 'loading':
        return {
          className: 'status-card--loading',
          badge: 'Checking...',
          badgeClass: 'status-badge--loading',
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div className={`status-card ${config.className}`}>
      <div className="status-card__header">
        <div className="status-card__title-row">
          <div className="status-card__title-group">
            <span className="status-card__icon">{icon}</span>
            <h3 className="status-card__title">{title}</h3>
          </div>
          <span className={`status-badge ${config.badgeClass}`}>{config.badge}</span>
        </div>
      </div>

      <div className="status-card__content">
        {status === 'loading' && <p className="status-card__message">Checking connection...</p>}

        {status === 'warning' && warningMessage && (
          <p className="status-card__message status-card__message--warning">{warningMessage}</p>
        )}

        {status === 'disconnected' && !warningMessage && onConnect && (
          <div className="status-card__action-area">
            <button onClick={onConnect} className="status-card__button" disabled={isLoading}>
              {isLoading ? 'Connecting...' : connectLabel}
            </button>
          </div>
        )}

        {status === 'connected' && (
          <div className="status-card__info">
            {accountInfo && (
              <div className="status-card__info-item">
                <span className="status-card__info-label">Account:</span>
                <span className="status-card__info-value">{accountInfo}</span>
              </div>
            )}
            {additionalInfo && (
              <div className="status-card__info-item">
                <span className="status-card__info-label">Connected:</span>
                <span className="status-card__info-value">{additionalInfo}</span>
              </div>
            )}
            {onDisconnect && (
              <button onClick={onDisconnect} className="status-card__button status-card__button--disconnect">
                Disconnect
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
